import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { fetchBillingAccounts } from '../../common/fetch.js';
import {
  billingAddressTitleMsg,
  cancelMsg,
  confirmMsg,
  consumerOmaElisaMsg,
  editMsg,
  myEquipmentInvoice,
  streetAddressMsg,
  t,
} from '../../common/i18n';
import { PostOffice } from '../../common/react-hook-form/fields/PostOffice';
import { StreetAddress } from '../../common/react-hook-form/fields/StreetAddress';
import { Email, PostalCode } from '../../common/react-hook-form/fields/index.js';
import { findPersonBillingAccount } from '../../common/utils/stateUtils';
import { AuthenticatedUserRole, BillingAccountDeliveryMethod } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider';
import { upsertPersonBillingAccount } from '../../selfservice/actions';
import type { BillingAccount } from '../../generated/api/models.js';

interface OmaLaskuDeviceFormValues {
  email: string;
  line1: string;
  line2?: string;
  postalCode: string;
  postOffice: string;
}

interface FieldSetProps {
  billingAccount: BillingAccount;
  isEdit: boolean;
}

const BillingAddressHeading = () => (
  <h4 className={`${dsClass.H5} ${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_2}`}>
    {t.PS5T(billingAddressTitleMsg)}
  </h4>
);

const EmailFieldSet = ({ isEdit, billingAccount }: FieldSetProps) => {
  if (isEdit) {
    return <Email label={t.PS5T(billingAddressTitleMsg)} name="email" readonly={false} required />;
  }

  return (
    <>
      <BillingAddressHeading />
      <div>{billingAccount.billReceiverEmail}</div>
      <div className={`${dsClass.COLOR_NEUTRAL_600} ${dsClass.MARGIN_TOP_2}`}>
        {t.CUS1('The invoice will be sent to your preferred email address.')}
      </div>
    </>
  );
};

const PostAddressFieldset = ({ billingAccount, isEdit }: FieldSetProps) => {
  if (isEdit) {
    return (
      <>
        <StreetAddress name="line1" required />
        <StreetAddress name="line2" label={`${t.DD38(streetAddressMsg)} 2`} required={false} />
        <PostalCode name="postalCode" required />
        <PostOffice name="postOffice" required />
      </>
    );
  }

  return (
    <>
      <BillingAddressHeading />
      <div>{billingAccount.billReceiverName}</div>
      <div>{billingAccount.payerAddress.line1}</div>
      <div>{billingAccount.payerAddress?.line2}</div>
      <div>
        {billingAccount.payerAddress.postalCode} {billingAccount.payerAddress.postOffice}
      </div>
      <div className={`${dsClass.COLOR_NEUTRAL_600} ${dsClass.MARGIN_TOP_2}`}>
        {t.PD2Y('The invoice will be sent as a paper copy to your preferred address.')}
      </div>
    </>
  );
};

const EinvoiceFieldset = () => {
  return (
    <>
      <BillingAddressHeading />
      <div>{t.UWZ8('E-invoice')}</div>
      <div className={dsClass.MARGIN_TOP_2}>
        <span className={dsClass.COLOR_NEUTRAL_600}>
          {t.S4HQ('You can manage e-invoicing in your personal online bank.')}&nbsp;
        </span>
      </div>
    </>
  );
};

const PersonBaDetails = ({ billingAccount, isEdit }: FieldSetProps) => {
  const getComponent = () => {
    switch (billingAccount.deliveryMethod) {
      case 'EMAIL':
        return <EmailFieldSet billingAccount={billingAccount} isEdit={isEdit} />;
      case 'PAPER':
        return <PostAddressFieldset billingAccount={billingAccount} isEdit={isEdit} />;
      case 'ELECTRONIC':
        return <EinvoiceFieldset />;
    }
    return undefined;
  };

  return (
    <CL.GridRow className={`${dsClass.MARGIN_TOP_6} ${dsClass.PADDING_0}`}>
      <CL.GridCol className={dsClass.PADDING_0} colWidthXS={4} colWidthS={4} colWidthL={6}>
        {getComponent()}
      </CL.GridCol>
    </CL.GridRow>
  );
};

interface OmalaskuDeviceInformationProps {
  billingAccount: BillingAccount;
}

const OmaLaskuDeviceForm = ({ billingAccount }: OmalaskuDeviceInformationProps) => {
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm<OmaLaskuDeviceFormValues>({
    defaultValues: {
      email: billingAccount.billReceiverEmail || '',
      line1: billingAccount.payerAddress.line1,
      line2: billingAccount.payerAddress.line2,
      postalCode: billingAccount.payerAddress.postalCode,
      postOffice: billingAccount.payerAddress.postOffice,
    },
  });
  const { handleSubmit, reset: resetForm } = methods;
  const allowEdit = [BillingAccountDeliveryMethod.EMAIL, BillingAccountDeliveryMethod.PAPER].includes(
    billingAccount.deliveryMethod
  );

  const onSubmit = (values: OmaLaskuDeviceFormValues) => {
    setIsSubmitting(true);
    dispatch(
      upsertPersonBillingAccount(
        {
          address: {
            line1: values.line1,
            line2: values.line2,
            postalCode: values.postalCode,
            postOffice: values.postOffice,
            countryCode: 'FIN',
          },
          email: values.email ?? '',
        },
        billingAccount.billingAccountId,
        undefined,
        () => {
          setIsSubmitting(false);
          setIsEdit(false);
        }
      )
    );
  };

  const onCancelEdit = () => {
    setIsEdit(false);
    resetForm();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PersonBaDetails isEdit={isEdit} billingAccount={billingAccount} />
        {isEdit && (
          <CL.GridRow>
            <CL.GridCol colWidthXS={8} className={dsClass.PADDING_0}>
              <CL.Button type="submit" loading={isSubmitting}>
                {t.QVYK(confirmMsg)}
              </CL.Button>
              <CL.Button
                type="button"
                color="secondary"
                disabled={isSubmitting}
                className={dsClass.MARGIN_LEFT_4}
                onClick={onCancelEdit}
              >
                {t.B2V1(cancelMsg)}
              </CL.Button>
            </CL.GridCol>
          </CL.GridRow>
        )}
      </form>
      {allowEdit && !isEdit && (
        <CL.GridRow className={dsClass.MARGIN_TOP_4}>
          <CL.GridCol colWidthXS={8} className={dsClass.PADDING_0}>
            <CL.Button color="light" onClick={() => setIsEdit(true)}>
              {t.NVPK(editMsg)}
            </CL.Button>
          </CL.GridCol>
        </CL.GridRow>
      )}
      <CL.Disclaimer
        className={`of-employee-omalasku__delivery-method-disclaimer ${dsClass.MARGIN_TOP_4}`}
        disclaimerType="info"
        icon={<CL.Icon icon="information" size="m" />}
        title={t.OEEF('Changes to billing method')}
      >
        <CL.List listType="unordered">
          <li>
            <span className={dsClass.FONT_SIZE_MEDIUM}>
              {t.TZPN(
                'You can order paper invoicing (21,32 €/invoice) or activate email invoicing through our customer service.'
              )}
            </span>
            &nbsp;
            <CL.Link linkHref={paths.CONTACT_INFO} linkTarget="_blank" className={dsClass.FONT_SIZE_MEDIUM}>
              {t.B0D9('Customer service contact details.')}
            </CL.Link>
          </li>
          <li>
            <span className={dsClass.FONT_SIZE_MEDIUM}>
              {t.M9Y9('You can activate e-invoicing in your personal online bank.')}
            </span>
            &nbsp;
            <CL.Link
              linkHref={t.A8V9('https://elisa.fi/asiakaspalvelu/english/invoice-questions/#einvoice')}
              linkTarget="_blank"
              className={dsClass.FONT_SIZE_MEDIUM}
            >
              {t.AAOV('Instructions for activating the e-invoice.')}
            </CL.Link>
          </li>
        </CL.List>
      </CL.Disclaimer>
    </FormProvider>
  );
};

const DeviceInfoContent = ({ billingAccount }: OmalaskuDeviceInformationProps) => {
  return (
    <>
      {t.AUI3('You can view and pay your invoice in')} &nbsp;
      <CL.Link className={dsClass.FONT_SIZE_MEDIUM} linkTarget="_blank" linkHref="https://verkkoasiointi.elisa.fi">
        {t.QE2O(consumerOmaElisaMsg)}
        <CL.Icon icon="external-link" size="s" type="regular" color="blue-600" />
      </CL.Link>
      <OmaLaskuDeviceForm billingAccount={billingAccount} />
    </>
  );
};

const NoDeviceInfoContent = () => {
  return (
    <CL.Link
      className={dsClass.FONT_SIZE_MEDIUM}
      linkTarget="_blank"
      linkHref="https://yrityksille.elisa.fi/oma-lasku-tyosuhdeliittymaan"
    >
      {t.TFMQ('More info about Oma Laitelasku')}
    </CL.Link>
  );
};

export const OmalaskuDeviceInformation = () => {
  const [billingAccounts, setBillingAccounts] = useState<BillingAccount[] | null>(null);
  const { authenticatedUser } = useAuth();
  const billingAccount = billingAccounts ? findPersonBillingAccount(billingAccounts) : undefined;
  const isPunchoutUser = authenticatedUser?.userRole === AuthenticatedUserRole.PUNCHOUT_USER;

  useEffect(() => {
    if (authenticatedUser?.businessId) {
      fetchBillingAccounts({ useSearchService: true }).then(response => {
        setBillingAccounts(response.billingAccounts || []);
      });
    }
  }, [authenticatedUser?.businessId]);

  return (
    <>
      <div className={dsClass.MARGIN_TOP_6}>
        <h3 className={dsClass.DESCRIPTION_TITLE}>{t.M1N1(myEquipmentInvoice)}</h3>
        <p>
          {t.NY4L(
            `With Elisa Oma Laitelasku, you can choose a device that is better than your employer's standard device. You pay the difference in price each month in a separate invoice.`
          )}
        </p>
      </div>
      {billingAccount && !isPunchoutUser ? (
        <DeviceInfoContent billingAccount={billingAccount} />
      ) : (
        <NoDeviceInfoContent />
      )}
    </>
  );
};
